import type { BillItem } from '@bbpos/types';
import type { CartLineItem } from '@/stores/use-cart-store';
import type { UpdateDraftBillDto } from '@/services/billing.service';

type ServerLine = Pick<BillItem, 'id' | 'product_id' | 'quantity' | 'line_discount'>;
type CartLine = Pick<CartLineItem, 'productId' | 'quantity' | 'discountAmount'>;

/**
 * Turn the difference between a server DRAFT's lines and the cart into one
 * `PATCH /bills/:id` body. Lines are joined on `product_id` (the cart and
 * `POST /bills` both keep one line per product):
 *
 *   - server line with no cart line            → `remove_item_ids`
 *   - cart line with no server line            → `add_items` (priced by the server)
 *   - both, quantity or discount differs       → `update_items` (in place —
 *     keeps the line's original price snapshot)
 *
 * Returns `null` when nothing differs, so an untouched draft is never rewritten.
 * BIGINT columns arrive as strings; they are coerced here.
 */
export function diffDraftLines(
  serverItems: ServerLine[],
  cartItems: CartLine[],
): UpdateDraftBillDto | null {
  const cartByProduct = new Map(cartItems.map((line) => [line.productId, line]));

  const remove_item_ids: string[] = [];
  const update_items: NonNullable<UpdateDraftBillDto['update_items']> = [];
  const seenProducts = new Set<string>();

  for (const item of serverItems) {
    const cartLine = cartByProduct.get(item.product_id);
    // A second server line for the same product can't be represented in the
    // cart (it merges per product) — drop the extra.
    if (!cartLine || seenProducts.has(item.product_id)) {
      remove_item_ids.push(item.id);
      continue;
    }
    seenProducts.add(item.product_id);
    const quantity = Number(item.quantity);
    const discount = Number(item.line_discount ?? 0);
    if (quantity !== cartLine.quantity || discount !== cartLine.discountAmount) {
      // Always send both so a cleared discount lands as 0.
      update_items.push({ item_id: item.id, quantity: cartLine.quantity, discount: cartLine.discountAmount });
    }
  }

  const add_items = cartItems
    .filter((line) => !seenProducts.has(line.productId))
    .map((line) => ({
      product_id: line.productId,
      quantity: line.quantity,
      ...(line.discountAmount > 0 ? { discount: line.discountAmount } : {}),
    }));

  if (remove_item_ids.length === 0 && add_items.length === 0 && update_items.length === 0) {
    return null;
  }
  return {
    ...(remove_item_ids.length > 0 ? { remove_item_ids } : {}),
    ...(add_items.length > 0 ? { add_items } : {}),
    ...(update_items.length > 0 ? { update_items } : {}),
  };
}
